import { Avatar, Box, Button, Flex, Heading, HStack, Icon, SimpleGrid, Text, useToast, VStack } from "@chakra-ui/react";
import { observer } from "mobx-react-lite";
import React, { useState } from "react";
import { Link } from "react-router-dom";
import {
  PiCameraDuotone as FiCamera,
  PiSealCheckDuotone as FiCheckCircle,
  PiDownloadSimpleDuotone as FiDownload,
  PiUploadSimpleDuotone as FiUpload,
  PiArrowSquareOutDuotone as FiExternalLink
} from "react-icons/pi";
import { bindProperty, FilePreview } from "@lubbezposrednio-frontend/core";
import { CustomFormControl, CustomInput } from "@lubbezposrednio-frontend/commons";
import { ProfileViewModel } from "../../ProfileViewModel";
import { useImageDropzone } from "../../../../hooks/useImageDropzone";
import { usePdfDropzone } from "../../../../hooks/usePdfDropzone";
import ImageCropModal from "../../../../components/ImageCropModal";

const Card: React.FC<{ title?: string; children: React.ReactNode }> = ({ title, children }) => (
  <Box borderRadius="18px" bg="rgba(24,12,48,0.028)" p={{ base: "18px", md: "22px" }}>
    {title && (
      <Heading fontSize="17px" fontWeight="800" color="#241536" mb="18px" letterSpacing="-0.01em">
        {title}
      </Heading>
    )}
    {children}
  </Box>
);

interface OrganizationProfileProps {
  model: ProfileViewModel;
}

export const OrganizationProfile: React.FC<OrganizationProfileProps> = observer(({ model }) => {
  const toast = useToast();
  const [cropFile, setCropFile] = useState<File | null>(null);
  const image = useImageDropzone(f => setCropFile(f), toast);
  const onCropped = (f: File) => model.setOrganizationProfilePhoto(Object.assign(f, { src: URL.createObjectURL(f) }) as FilePreview);
  const pdf = usePdfDropzone(model.setOrganizationStatement, toast);

  return (
    <VStack align="stretch" spacing="16px" w="100%">
      {/* Nagłówek organizacji */}
      <Card>
        <Flex align="center" gap="20px" wrap="wrap">
          <Box position="relative" flexShrink={0}>
            <input {...image.getInputProps()} />
            <ImageCropModal file={cropFile} isOpen={!!cropFile} onClose={() => setCropFile(null)} onCropped={onCropped} title="Logo organizacji" />
            <Avatar size="xl" name={model.organizationName || "?"} src={model.organizationPhotoPreview ?? undefined} bg="primary" color="white" />
            <Flex as="button" type="button" onClick={image.open} aria-label="Zmień logo" position="absolute" bottom="-2px" right="-2px" w="34px" h="34px" borderRadius="full" bg="accent" color="white" align="center" justify="center" border="3px solid white" _hover={{ bg: "#c41a3a" }}>
              <Icon as={FiCamera} boxSize="15px" />
            </Flex>
          </Box>
          <Box flex="1" minW={0}>
            <HStack spacing="9px" mb="2px" wrap="wrap">
              <Heading fontSize={{ base: "20px", md: "23px" }} fontWeight="800" color="primary" noOfLines={1}>
                {model.organizationName || "Twoja organizacja"}
              </Heading>
              {model.isOrganisationVerified && (
                <HStack spacing="5px" bg="rgba(47,206,37,0.12)" color="#1f9e18" px="9px" py="3px" borderRadius="full">
                  <Icon as={FiCheckCircle} boxSize="13px" />
                  <Text fontSize="12px" fontWeight="700">
                    Zweryfikowana
                  </Text>
                </HStack>
              )}
            </HStack>
            <Text fontSize="14px" color="gray.500" noOfLines={1}>
              {model.email}
            </Text>
            <Button onClick={image.open} size="sm" mt="12px" variant="outline" borderColor="primary" color="primary" borderRadius="10px" fontWeight="700" leftIcon={<FiCamera />} _hover={{ bg: "rgba(67,0,152,0.05)" }}>
              Zmień logo
            </Button>
          </Box>
        </Flex>
      </Card>

      {/* Dane organizacji */}
      <Card title="Dane organizacji">
        <SimpleGrid columns={{ base: 1, sm: 2, lg: 3 }} spacing="16px">
          <CustomFormControl labelTitle="Nazwa organizacji">
            <CustomInput autoComplete="organization" {...bindProperty(model, "organizationName")} variant="profile" />
          </CustomFormControl>
          <CustomFormControl labelTitle="KRS">
            <CustomInput {...bindProperty(model, "organizationKrs")} variant="profile" />
          </CustomFormControl>
          <CustomFormControl labelTitle="NIP">
            <CustomInput {...bindProperty(model, "organizationNip")} variant="profile" />
          </CustomFormControl>
          <CustomFormControl labelTitle="REGON">
            <CustomInput {...bindProperty(model, "organizationRegon")} variant="profile" />
          </CustomFormControl>
          <CustomFormControl labelTitle="Adres">
            <CustomInput autoComplete="street-address" {...bindProperty(model, "organizationAdress")} variant="profile" />
          </CustomFormControl>
          <CustomFormControl labelTitle="Miasto">
            <CustomInput autoComplete="address-level2" {...bindProperty(model, "organizationCity")} variant="profile" />
          </CustomFormControl>
          <CustomFormControl labelTitle="Kod pocztowy">
            <CustomInput autoComplete="postal-code" {...bindProperty(model, "organizationPostCode")} variant="profile" />
          </CustomFormControl>
          <CustomFormControl labelTitle="Nr telefonu">
            <CustomInput autoComplete="tel" {...bindProperty(model, "organizationPhoneNumber")} variant="profile" />
          </CustomFormControl>
        </SimpleGrid>
      </Card>

      {/* Oświadczenie organizacji */}
      <Card title="Oświadczenie">
        <Text fontSize="14px" color="gray.500" mb="16px">
          Pobierz wzór, podpisz i wgraj — potrzebne do weryfikacji organizacji.
        </Text>
        <input {...pdf.getInputProps()} />
        <Flex gap="12px" wrap="wrap">
          <Button onClick={model.downloadAffirmationTemplate} variant="outline" borderColor="primary" color="primary" borderRadius="12px" fontWeight="700" leftIcon={<FiDownload />} _hover={{ bg: "rgba(67,0,152,0.05)" }}>
            Pobierz wzór
          </Button>
          <Button onClick={pdf.open} bg="primary" color="white" borderRadius="12px" fontWeight="700" leftIcon={<FiUpload />} _hover={{ bg: "#36007a" }}>
            Wgraj podpisane
          </Button>
          {model.organizationStatementUrl && (
            <Button as={Link} to={model.organizationStatementUrl} target="_blank" variant="ghost" color="gray.500" borderRadius="12px" fontWeight="700" leftIcon={<FiExternalLink />} _hover={{ color: "primary", bg: "rgba(67,0,152,0.04)" }}>
              Otwórz wgrane
            </Button>
          )}
        </Flex>
      </Card>
    </VStack>
  );
});
